/**
 * Notifications: what Morcast tells the signed-in wallet. Data for the header's bell and for the
 * notifications page.
 *
 * The API decides which notices a wallet sees and which of them are unread. The list is read
 * again every minute, so a new notice shows without a reload. Opening the bell or the page marks
 * every notice read, which clears the count. The notices that were unread keep their mark until
 * the list is read again, so the reader still sees which ones are new.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api/client.js";
import { useSession } from "../auth/session.js";

/** A notice, as `GET /api/notifications` describes it. */
export interface Notification {
  id: number;
  /** The kind of event, as `<subject>.<what happened>`, such as `request.accepted`. */
  kind: string;
  title: string;
  body: string;
  /** A path in the app or an https address. Null when the notice leads nowhere. */
  link: string | null;
  /** ISO 8601 time. */
  createdAt: string;
  /** Whether it came after the wallet last opened its notifications. */
  unread: boolean;
}

/** The wallet's notices, the newest first, and how many of all its notices are unread. */
export interface NotificationList {
  unread: number;
  notifications: Notification[];
}

/** Query key of every list of notices in the React Query cache. */
export const NOTIFICATIONS_QUERY_KEY = ["notifications"] as const;

/** Notices the bell shows, and the page. */
export const BELL_NOTICES = 8;
export const PAGE_NOTICES = 100;

/** How often the lists are read again. */
const REFRESH_MS = 60_000;

/** The signed-in wallet's notices. Nothing is asked while nobody is signed in. */
export function useNotifications(limit: number) {
  const signedIn = Boolean(useSession().data);
  return useQuery({
    queryKey: [...NOTIFICATIONS_QUERY_KEY, limit],
    queryFn: async () => {
      const list = await api.get<NotificationList>(`/api/notifications?limit=${limit}`);
      // The bell is on every page, so an answer of another shape must fail here and not there.
      if (!Array.isArray(list?.notifications)) {
        throw new Error("The notifications could not be read.");
      }
      return list;
    },
    enabled: signedIn,
    refetchInterval: REFRESH_MS,
  });
}

/**
 * Marks every notice read. The count drops to zero at once in every cached list, and the
 * notices keep their marks until `refresh` reads the lists again.
 */
export function useMarkNotificationsRead() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => api.post<{ readAt: string }>("/api/notifications/read"),
    onSuccess: () => {
      queryClient.setQueriesData<NotificationList>({ queryKey: NOTIFICATIONS_QUERY_KEY }, (list) =>
        list ? { ...list, unread: 0 } : list,
      );
    },
  });
  return {
    markRead: mutation.mutate,
    /** Reads the lists again, which removes the marks of what was read. */
    refresh: () => queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY }),
  };
}

/** Whether a notice's link leaves the app. */
export function isExternalLink(link: string): boolean {
  return link.startsWith("https://");
}
